<template>
    <div>

        <b-breadcrumb>
            <b-breadcrumb-item to="/">
                <i class="fa fa-home"></i>
                Home
            </b-breadcrumb-item>
            <b-breadcrumb-item to="/Facilities/Offices">Facilities</b-breadcrumb-item>
            <b-breadcrumb-item active>Offices</b-breadcrumb-item>
        </b-breadcrumb>

        <div class="card">
            <div class="bg-dark card-header text-white">Offices</div>
            <div class="card-body">
                <!-- The collapsable filter options -->
                <AppFormCardCollapsable :display-footer="true" @submit.prevent="onFilterSubmit()" class="mb-3">
                    <template v-slot:header>Filter</template>
                    <template v-slot:body>
                        <div class="form-row">
                            <div class="col-md-4 col-sm-6 form-group">
                                <label>Name</label>
                                <input
                                        class="form-control"
                                        placeholder="Office Name"
                                        v-model="filterOptions.name"
                                />
                            </div>
                            <div class="col-md-4 col-sm-6 form-group">
                                <label>Code</label>
                                <input
                                        class="form-control"
                                        placeholder="TAMU Building Code"
                                        v-model="filterOptions.code"
                                />
                            </div>
                            <div class="col-md-4 col-sm-6 form-group">
                                <label>City</label>
                                <input
                                        class="form-control"
                                        placeholder="Address City"
                                        v-model="filterOptions.city"
                                />
                            </div>
                        </div>
                        <div>
                            <div class="col-md-3 form-group">
                                <AppCheckBox
                                        v-model="filterOptions.onlyShowTTIOffices"
                                >Only Show TTI Offices
                                </AppCheckBox>
                            </div>
                        </div>
                    </template>
                    <template v-slot:footer>
                        <button class="btn btn-primary" type="submit">Apply</button>
                    </template>
                </AppFormCardCollapsable>

                <div class="d-print-none dropdown mb-3 text-right">
                    <button aria-expanded="false" aria-haspopup="true" class="btn btn-secondary dropdown-toggle"
                            data-toggle="dropdown" style="width: 100px" type="button">
                        <span>Actions</span>
                    </button>
                    <div class="dropdown-menu">
                        <RouterLink :to="{name: 'AddBuilding'}" class="btn btn-outline-light dropdown-item text-left"
                                    title="Add new Office">
                            <i class="fa fa-plus"></i>Add
                        </RouterLink>
                    </div>
                </div>

                <BTable
                        :busy="isLoading"
                        :fields="tableFields"
                        :items="buildings"
                        :responsive="true"
                        primary-key="id"
                        show-empty
                        striped
                        table-class="table-align-middle">

                    <template v-slot:cell(id)="row">
                        <div class="btn-group">
                            <router-link
                                    :to="{name:'BuildingDetails',params:{id:row.item.id, buildingName:row.item.name, isTTIOffice:row.item.isTTIOffice}}"
                                    class="btn btn-sm btn-warning d-print-none"
                                    data-toggle="tooltip"
                                    title="Edit office details">
                                <i class="fa fa-pencil-alt"></i>Edit
                            </router-link>
                            <button @click="onDeleteClick(row.item)" class="btn btn-danger btn-sm d-print-none"
                                    type="button"><i class="fa fa-trash"></i>Delete
                            </button>
                        </div>
                    </template>

                    <template v-slot:cell(isTTIOffice)="row">
                        <AppDisplayTemplateBoolean :value="row.item.isTTIOffice"></AppDisplayTemplateBoolean>
                    </template>

                    <!-- Footer Pagination -->
                    <template v-slot:custom-foot>
                        <b-tr>
                            <b-td :colspan="tableFields.length">
                                <AppTablePagination
                                        :page.sync="filterOptions.page"
                                        :pageSize.sync="filterOptions.pageSize"
                                        :totalCount.sync="totalBuildings"
                                        v-on:update:page="loadData()"
                                        v-on:update:pageSize="loadData()"
                                ></AppTablePagination>
                            </b-td>
                        </b-tr>
                    </template>

                </BTable>

            </div>

            <AppModalConfirm :visible="deletingRow" @close="deletingRow=null" @confirm="onDeleteConfirmed(deletingRow)">
                <template v-slot:header>Confirm</template>
                <p v-if="deletingRow">Are you sure you want to delete '{{deletingRow.name}}'?</p>
            </AppModalConfirm>
        </div>

    </div>
</template>

<script>
    import AppCheckBox from "../../components/shared/AppCheckBox";
    import AppFormCardCollapsable from "../../components/shared/AppFormCardCollapsable";
    import AppTablePagination from "../../components/shared/AppTablePagination";
    import {ToastType} from "@/services/models/shared/ToastType";
    import AddressService from "@/services/AddressService";
    import AppModalConfirm from "@/components/shared/AppModalConfirm";
    import AppDisplayTemplateBoolean from "@/components/shared/AppDisplayTemplateBoolean";

    export default {
        components: {
            AppFormCardCollapsable,
            AppTablePagination,
            AppModalConfirm,
            AppDisplayTemplateBoolean,
            AppCheckBox
        },
        data() {
            return {
                buildings: [],
                totalBuildings: 0,
                deletingRow: null,
                filterOptions: {
                    page: 1,
                    pageSize: 10,
                    name: null,
                    code: null,
                    city: null,
                    onlyShowTTIOffices: false
                },
                isLoading: false,
                tableFields: [
                    {
                        key: 'id',
                        label: '',
                        thStyle: 'width:175px'
                    },
                    {
                        key: 'isTTIOffice',
                        label: 'Maintained by TTI'
                    },
                    {
                        key: 'name',
                        label: 'Office Name',
                    },
                    {
                        key: 'shortName',
                        label: 'Short Name',
                    },
                    {
                        key: 'code',
                        label: 'TAMU Building Code',
                    },
                    {
                        key: 'city',
                        label: 'City',
                    },
                    {
                        key: 'stateCode',
                        label: 'State',
                    },
                    {
                        key: 'zipCode',
                        label: 'Zip Code',
                    },
                ]
            }
        },
        methods: {
            loadData() {
                const vm = this;
                vm.isLoading = true;

                AddressService.getBuildingList(vm.filterOptions)
                    .then(response => {
                        vm.buildings = response.data;
                        vm.totalBuildings = response.totalCount;
                    }).catch(() => {
                    vm.redirectToError()
                }).finally(() => {
                    vm.isLoading = false;
                })
            },
            /**
             * @description Resets the page to the first page and reloads the data
             */
            onFilterSubmit() {
                this.filterOptions.page = 1;
                this.loadData();
            },

            /**
             * @description Opens up a modal to confirm deletion of the building
             */
            onDeleteClick(building) {
                this.deletingRow = building;
            },

            /**
             * @description When the user clicks 'Confirm' - the selected building will be deleted and the modal will be closed.
             */
            onDeleteConfirmed(deletingRow) {
                const vm = this;

                AddressService.deleteBuilding(deletingRow.id)
                    .then(() => {
                        vm.alert(ToastType.Success, `The building '${deletingRow.name}' was successfully deleted`);

                        // Reload the data on the page
                        vm.loadData();
                    }).catch(error => {
                    vm.alert(ToastType.Danger, error.response.statusText)
                }).finally(() =>
                    vm.deletingRow = null);
            }
        },
        created() {
            this.loadData();
        }
    }
</script>
